/**
 * usePlanFeatures — hook for the current org's plan-feature map.
 *
 * Fetches once per session (cached in a module-level Promise to dedupe
 * concurrent calls from multiple components on first render). The
 * BE endpoint /api/v1/subscriptions/features always returns every
 * registered feature key with a boolean — so missing keys quietly
 * resolve to `false`, matching the BE's fail-closed contract.
 *
 * Exports:
 *   usePlanFeatures()        — { features, metadata, isLoading, error, refetch }
 *   useFeatureEnabled(key)   — true / false (false during initial load)
 *
 * Convention for callers: use `useFeatureEnabled` for hide/show
 * decisions. Use `usePlanFeatures` only when you need the full map
 * (e.g. admin matrix preview).
 */
import { useCallback, useEffect, useState } from "react";
import { fetchData } from "../api/apiService";
import { SUBSCRIPTIONS } from "../api/endpointMap";
import { useAuth } from "../context/AuthContext";
import { FEATURE_KEYS } from "../utils/featureKeys";
import { APPEAL_FEATURE_ENABLED } from "../utils/constants";

// Module-level cache. One Promise per session — every consumer awaits
// the same fetch. Reset by refetch() to force a re-pull (used after a
// plan change in self-service upgrade flow, Phase 2).
let _featuresPromise = null;
let _featuresCache = null;

function _unwrap(raw) {
  // subscriptions._response packs payload under `result`. Defensive
  // fallback to `data` in case the envelope ever changes.
  const payload = raw?.result || raw?.data || {};
  return {
    features: payload.features || {},
    metadata: payload.metadata || {},
  };
}

function _loadFeatures(force = false) {
  if (force) {
    _featuresPromise = null;
    _featuresCache = null;
  }
  if (_featuresPromise) return _featuresPromise;
  _featuresPromise = fetchData(SUBSCRIPTIONS.MY_FEATURES)
    .then((raw) => {
      _featuresCache = _unwrap(raw);
      return _featuresCache;
    })
    .catch((err) => {
      // On error, clear the cached promise so a retry can reattempt.
      _featuresPromise = null;
      throw err;
    });
  return _featuresPromise;
}

export function usePlanFeatures() {
  const [state, setState] = useState({
    features: _featuresCache?.features || {},
    metadata: _featuresCache?.metadata || {},
    isLoading: !_featuresCache,
    error: null,
  });

  const refetch = useCallback(() => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    _loadFeatures(true)
      .then(({ features, metadata }) =>
        setState({ features, metadata, isLoading: false, error: null }),
      )
      .catch((err) =>
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err?.detail || err?.message || "Failed to load plan features",
        })),
      );
  }, []);

  useEffect(() => {
    let cancelled = false;
    _loadFeatures()
      .then(({ features, metadata }) => {
        if (!cancelled) {
          setState({ features, metadata, isLoading: false, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err?.detail || err?.message || "Failed to load plan features",
          }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...state, refetch };
}

/**
 * Convenience boolean hook. Returns false during initial load and on
 * error — fail-closed, matches BE behaviour. Use this for the common
 * "hide this button if the plan lacks the feature" pattern.
 */
export function useFeatureEnabled(featureKey) {
  const { features, isLoading } = usePlanFeatures();
  if (isLoading) return false;
  return Boolean(features[featureKey]);
}

/**
 * Capability gate (2026-07-11) — the AND of BOTH tiers:
 *
 *   org tier   plan feature   (AI_RESPONSE_GENERATION / *_APPEAL_DRAFTING)
 *   user tier  user flag      (per-portal enable_* capability flag)
 *
 * This is the ONLY place the two tiers are combined. Callers must use
 * this rather than re-implementing the AND, so the org and user halves
 * can never drift apart across screens.
 *
 * Fail-closed on the org tier (useFeatureEnabled already returns false
 * while loading). Fail-OPEN on the user tier when the flag is absent —
 * a session cached before the migration has no flag, and the column
 * defaults to true server-side, so `undefined` must mean "allowed".
 *
 * @param {string} featureKey  FEATURE_KEYS.* (org/plan tier)
 * @param {string} userFlag    the user-object boolean field name
 */
export function useCapabilityEnabled(featureKey, userFlag) {
  const planAllows = useFeatureEnabled(featureKey);
  const { user } = useAuth();
  if (!planAllows) return false;
  return (user?.[userFlag] ?? true) === true;
}

/**
 * Named wrappers so call sites read declaratively and can't mismatch a
 * feature key with the wrong user flag.
 *
 * Per-portal since 2026-07-15: each capability is gated by a portal-specific
 * user flag, so callers pass the portal ("income_tax" | "gst" | "tds").
 * Response Generation shares one plan feature (AI_RESPONSE_GENERATION) across
 * portals; Prepare Appeal's plan feature differs per portal (GST vs IT).
 */
const _RG_USER_FLAG = {
  income_tax: "enable_response_generation_income_tax",
  gst: "enable_response_generation_gst",
  tds: "enable_response_generation_tds",
};
const _PA_USER_FLAG = {
  income_tax: "enable_prepare_appeal_income_tax",
  gst: "enable_prepare_appeal_gst",
};
const _PA_FEATURE_KEY = {
  income_tax: FEATURE_KEYS.IT_APPEAL_DRAFTING,
  gst: FEATURE_KEYS.GST_APPEAL_DRAFTING,
};

export function useResponseGenerationEnabled(portal = "gst") {
  return useCapabilityEnabled(
    FEATURE_KEYS.AI_RESPONSE_GENERATION,
    _RG_USER_FLAG[portal] || _RG_USER_FLAG.gst,
  );
}

export function usePrepareAppealEnabled(portal = "gst") {
  // Global Appeal master switch (production withholds the feature). Evaluate
  // the capability hook unconditionally to keep hook order stable, then
  // force-disable when the feature is globally off.
  const capabilityEnabled = useCapabilityEnabled(
    _PA_FEATURE_KEY[portal] || FEATURE_KEYS.GST_APPEAL_DRAFTING,
    _PA_USER_FLAG[portal] || _PA_USER_FLAG.gst,
  );
  return APPEAL_FEATURE_ENABLED && capabilityEnabled;
}
